/**
 * Приведение авторского HTML к виду, пригодному для обоих экспортов.
 *
 * Живёт на общем уровне, а не внутри pdf-ветки, потому что применяться обязано ОДИНАКОВО
 * к телу заметки и к названию сущности, в обоих форматах. Пока нормализация была
 *pdf-локальной, название с картинкой или таблицей давало разный результат: docx мог не
 * выпустить заголовок вовсе, а pdf выпускал заголовок с заглушкой.
 */

import { imagePlaceholderText, PLACEHOLDER_CLASS, PLACEHOLDER_TABLE } from './placeholders';

/**
 * Элементы, которые ломают разбор всего, что идёт после них: их «закрывающий» тег
 * при повторном разборе считается обычным текстом, поэтому такой элемент проглотил бы
 * следующие заметки целиком. Встречаются только в импортированном или вставленном
 * содержимом, но цена защиты — одна строка.
 */
const PARSER_BREAKING = 'plaintext, xmp, listing';

/**
 * Узлы, которые оба формата показывают заглушкой.
 *
 * Схема редактора их всё равно отбрасывает при первом редактировании, поэтому
 * поддержка была отложена. Важно, что заглушка одна на оба формата: иначе один
 * экспорт показывал бы «[Изображение]», а другой пытался бы отрисовать картинку.
 */
function replaceUnsupported(body: HTMLElement, doc: Document): void {
  // Заглушка инлайновая: картинка часто стоит внутри абзаца (<p><img></p>),
  // и вложенный <p> дал бы невалидную разметку.
  body.querySelectorAll('img').forEach((image) => {
    const mark = doc.createElement('em');

    mark.textContent = imagePlaceholderText(image.getAttribute('alt') ?? '');
    // Та же метка, что у таблицы: одиночная картинка вне <p> становится узлом верхнего
    // уровня, и без неё проход счёл бы заглушку прозой и выдал ей красную строку —
    // которой в docx у неё нет.
    mark.className = PLACEHOLDER_CLASS;
    image.replaceWith(mark);
  });

  body.querySelectorAll('table').forEach((table) => {
    const paragraph = doc.createElement('p');
    const mark = doc.createElement('em');

    mark.textContent = PLACEHOLDER_TABLE;
    // Метка для прохода красной строки: заглушка — не проза.
    paragraph.className = PLACEHOLDER_CLASS;
    paragraph.appendChild(mark);
    table.replaceWith(paragraph);
  });
}

/**
 * Прогоняет фрагмент через разбор и сериализацию.
 *
 * Простая склейка строк опасна: незакрытый тег в одной заметке (а такое встречается в
 * импортированном содержимом) утащил бы следующую заметку внутрь себя. Браузерный парсер
 * закрывает висящие теги, поэтому каждая сущность остаётся самостоятельной.
 */
export function normalizeFragment(html: string, parser: DOMParser = new DOMParser()): string {
  if ((html ?? '').trim() === '') {
    return '';
  }

  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');
  const { body } = doc;

  // Содержимое сохраняем, сам элемент разворачиваем: текст не теряется,
  // а способность съесть соседей — да.
  body.querySelectorAll(PARSER_BREAKING).forEach((element) => {
    element.replaceWith(...Array.from(element.childNodes));
  });

  replaceUnsupported(body, doc);

  return body.innerHTML;
}
